import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import api, { tenantApi } from '../services/api';
import NotificationsDropdown from './NotificationsDropdown';
import GlobalSearch from './GlobalSearch';

const drawerWidth = 180;

// Full menu items for Office role
const allMenuItems = [
  { text: 'Dashboard', path: '/dashboard', roles: ['Office'] },
    { text: 'New Quote', path: '/new-quote', roles: ['Office'] },
  { text: 'Projects', path: '/projects', roles: ['Office', 'Site', 'Site Manager'] },
  { text: 'Purchase Orders', path: '/purchase-orders', roles: ['Office', 'Site Manager'] },
  { text: 'Time Sheets', path: '/time-sheets', roles: ['Office'] },
    { text: 'My Time', path: '/my-time', roles: ['Office', 'Site', 'Site Manager'] },

  { text: 'Tools', path: '/tools', roles: ['Office'] },
  { text: 'AI Assistant', path: '/ai-assistant', roles: ['Office'] },
    { text: 'Workspace', path: '/workspace', roles: ['Office', 'Site Manager'] },

];

function Layout({ children, currentUser, onLogout, darkMode, toggleDarkMode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [hasDepartments, setHasDepartments] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Filter menu items based on user role
  const getMenuItemsForRole = (userRole) => {
    return allMenuItems.filter(item => item.roles.includes(userRole));
  };

  const menuItems = currentUser ? getMenuItemsForRole(currentUser.role) : [];

  useEffect(() => {
    let isMounted = true;

    const fetchDepartments = async () => {
      if (!currentUser?.tenant_id) {
        setHasDepartments(false);
        return;
      }

      try {
        const response = await tenantApi.getBranding();
        const departments = response.data?.branding?.departments;
        if (isMounted) {
          setHasDepartments(Array.isArray(departments) && departments.length > 0);
        }
      } catch (error) {
        console.error('Error fetching tenant departments:', error);
        if (isMounted) {
          setHasDepartments(false);
        }
      }
    };

    fetchDepartments();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.tenant_id]);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await api.authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local auth and call logout callback
      api.clearStoredAuth();
      onLogout();
      navigate('/login');
    }
    handleUserMenuClose();
  };

  const handleProfile = () => {
    navigate('/profile-settings');
    handleUserMenuClose();
  };
  
  

  const drawer = (
    <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Spacer to account for the unified header */}
      <Toolbar sx={{ 
        minHeight: { xs: 48, sm: 56 },
        height: { xs: 48, sm: 56 }
      }} />
      <List sx={{ flexGrow: 1, overflow: 'auto', px: 1, pt: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton 
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                py: 1,
                '&.Mui-selected': {
                  bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,135,193,0.08)' : 'rgba(0,135,193,0.16)',
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,135,193,0.12)' : 'rgba(0,135,193,0.24)',
                  },
                },
              }}
            >
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontSize: '0.875rem', 
                  fontWeight: location.pathname === item.path ? 600 : 500 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'divider' }} />
      {currentUser && (
        <Box sx={{ p: 2, position: 'relative' }}>
          <Typography variant="body2" fontWeight="bold">
            {currentUser.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {currentUser.email}
          </Typography>
          {currentUser.tenant_name && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {currentUser.tenant_name}
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <Chip
              label={hasDepartments && currentUser.department ? `${currentUser.role} | ${currentUser.department}` : currentUser.role}
              size="small"
              color={currentUser.role === 'Office' ? 'primary' : 'secondary'}
              variant="outlined"
            />
          </Box>
        </Box>
      )}

      {/* Tenant logo bottom-right of sidebar. Put a default path - replace with your tenant's logo in public/ */}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', overflowX: 'hidden', width: '100%' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          ml: 0,
          left: 0,
          bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(26,30,36,0.8)',
          backdropFilter: 'blur(12px)',
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 48, sm: 56 }, px: 2 }}>
          {/* Logo on the left - visible on desktop */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', mr: 3 }}>
            <RouterLink to="/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                src="/struxor.png"
                alt="STRUXOR"
                sx={{ 
                  height: 32, 
                  display: 'block',
                  filter: (theme) => theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
                }}
              />
            </RouterLink>
          </Box>
          
          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Global Search */}
          {currentUser && <GlobalSearch />}

          {currentUser && (
            <>
              {/* Dark Mode Toggle */}
              <IconButton
                color="inherit"
                onClick={toggleDarkMode}
                sx={{ ml: 1 }}
              >
                {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>

              {/* Notifications Dropdown */}
              <NotificationsDropdown />
              
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                sx={{ ml: 1 }}
              >
          <Avatar sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: 'primary.main', 
                  color: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}>
                  {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleProfile}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Profile Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none', bgcolor: 'background.paper' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth, 
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    <Box
        component="main"
        sx={{ 
      flexGrow: 1, 
      p: { xs: 1, sm: 2, md: 3 }, 
      width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
      overflowX: 'hidden'
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}

export default Layout;
